/**
 * src/components/Financial/RetrogradeSpeedSection.jsx
 * 【完全版：ダークモード対応・UI刷新・子コンポーネントへのデータ伝達】
 */

import React, { useMemo, useState, useEffect } from "react";
import * as Astro from "../../astronomy.js";
import {
  PLANETS,
  PLANET_SYMBOLS,
  DEFAULT_PLANET_COLORS,
} from "../../utils/constants.js";
import { styles as commonStyles } from "../../utils/styles.js";
import RetrogradeCalendarSection from "./RetrogradeCalendarSection.jsx";
import { Gauge } from "lucide-react";

export default function RetrogradeSpeedSection(props) {
  const date = props.date;
  const positions = props.positions;
  const planetColors = props.planetColors || DEFAULT_PLANET_COLORS;
  const signColors = props.signColors;

  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200,
  );
  useEffect(function () {
    const handleResize = function () {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return function () {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  const isMobile = windowWidth <= 768;

  // --- 1. ダークモード判定ロジックの追加 ---
  const isDark = (function () {
    const p = props.isDarkMode;
    if (p === true || p === "true" || p === "dark") return true;
    if (typeof document !== "undefined") {
      var el = document.documentElement;
      var bd = document.body;
      if (
        el.classList.contains("dark") ||
        bd.classList.contains("dark") ||
        el.getAttribute("data-theme") === "dark"
      )
        return true;
    }
    return false;
  })();

  const theme = {
    cardBg: isDark ? "#1c1f26" : "#ffffff",
    subBg: isDark ? "rgba(255, 255, 255, 0.03)" : "#f8fafc",
    border: isDark ? "#2d313d" : "#e2e8f0",
    text: isDark ? "#ffffff" : "#1e293b",
    subText: isDark ? "#9ca3af" : "#64748b",
  };

  const speedDetails = useMemo(
    function () {
      if (date === null || date === undefined) return [];
      return PLANETS.map(function (p) {
        const speedData = Astro.getPlanetSpeed(p, date);
        const avgSpeed = Astro.getAveragePlanetSpeed(p);
        const ratio = speedData
          ? Math.abs(Math.round((speedData.speedPerDay / avgSpeed) * 100))
          : 0;

        return {
          planet: p,
          speedPerDay: speedData ? speedData.speedPerDay : 0,
          isRetrograde: speedData ? speedData.isRetrograde : false,
          isStation: speedData ? speedData.isStation : false,
          ratio: ratio,
        };
      });
    },
    [date],
  );

  const getSpeedColor = function (s) {
    if (s.isStation === true) return "#f1c40f";
    if (s.isRetrograde === true) return "#e74c3c";
    if (s.ratio > 110) return "#27ae60";
    if (s.ratio < 90) return "#3498db";
    return theme.subText;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* メインカード：移動速度グリッド */}
      <div
        style={{
          background: theme.cardBg,
          borderRadius: "15px",
          border: "1px solid " + theme.border,
          padding: "25px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "25px",
            fontWeight: "bold",
            fontSize: "16px",
            color: theme.text,
          }}
        >
          <Gauge size={18} color="rgb(147, 39, 143)" />
          <span>現在天体の移動速度と状態</span>
        </div>

        <div
          style={{
            display: "grid",
            // ★修正：モバイル時は2列(1fr 1fr)、PC時は180pxの自動フィット
            gridTemplateColumns: isMobile
              ? "1fr 1fr"
              : "repeat(auto-fill, minmax(180px, 1fr))",
            gap: "12px",
          }}
        >
          {speedDetails.map(function (s) {
            const pColor = planetColors[s.planet] || theme.text;
            const barColor = getSpeedColor(s);

            return (
              <div
                key={s.planet}
                style={{
                  background: theme.subBg,
                  padding: "20px 15px",
                  borderRadius: "12px",
                  border: "1px solid " + theme.border,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  marginBottom:"10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                  }}
                >
                  <span style={{ fontSize: "24px", color: pColor }}>
                    {PLANET_SYMBOLS[s.planet]}
                  </span>
                  {s.isRetrograde === true && (
                    <span
                      style={{
                        color: "#e74c3c",
                        fontWeight: "bold",
                        fontSize: "14px",
                      }}
                    >
                      ℞
                    </span>
                  )}
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "bold",
                    color: theme.subText,
                    letterSpacing: "0.05em",
                  }}
                >
                  {s.planet.toUpperCase()}
                </div>

                <div
                  style={{
                    fontSize: "15px",
                    fontWeight: "900",
                    color: barColor,
                  }}
                >
                  {s.speedPerDay >= 0 ? "+" : ""}
                  {s.speedPerDay.toFixed(3)}°
                  <span style={{ fontSize: "10px", marginLeft: "2px" }}>
                    / d
                  </span>
                </div>

                <div
                  style={{
                    height: "6px",
                    background: isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0",
                    borderRadius: "3px",
                    margin: "5px 0",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: Math.min(100, s.ratio) + "%",
                      height: "100%",
                      background: barColor,
                      boxShadow: isDark
                        ? "0 0 10px " + barColor + "44"
                        : "none",
                      transition: "width 0.5s ease",
                    }}
                  />
                </div>

                <div
                  style={{
                    fontSize: "10px",
                    color: theme.subText,
                    fontWeight: "900",
                  }}
                >
                  {s.isStation === true
                    ? "STATION"
                    : s.isRetrograde === true
                      ? "RETROGRADE"
                      : "DIRECT"}
                  <span style={{ marginLeft: "4px", opacity: 0.7 }}>
                    ({s.ratio}%)
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 2. 逆行カレンダー（子コンポーネント）への伝達を修正 */}
      <RetrogradeCalendarSection
        planetColors={planetColors}
        signColors={signColors}
        isDarkMode={isDark} // ★ここでダークモードフラグを確実に渡す
      />
    </div>
  );
}
